import { isAnyOf } from "@reduxjs/toolkit";
import { GetAppListener } from "@shared/models/redux";
import { startImpersonationSession, stopImpersonationSession } from "@impersonateUser/store/actions";
import {
  changeDraftField,
  enterEditMode,
  exitEditMode,
  markRowForRemoval,
  undoRowEdit,
  unmarkRowForRemoval,
} from "@manageContacts/store/ducks";
import { editSelector } from "@manageContacts/store/selectors";
import { clearEditStateFromStorage, saveEditStateToStorage } from "@manageContacts/store/persistence";

/**
 * Mirrors the Manage Contacts edit-mode slice to `window.sessionStorage` so it
 * survives a hard browser refresh (F5) or tab session restore — the in-memory
 * Redux state already covers in-app navigation by itself.
 *
 * The listener fires AFTER each edit-mutating action so it reads the post-reducer
 * state directly via `getState()` instead of re-deriving the next snapshot from
 * the action payload. Writes happen on every edit action because the persistence
 * surface is tiny (3 small arrays per row) and edit interactions are
 * user-driven (not high-frequency), so debouncing would only add complexity.
 *
 * `exitEditMode` clears storage instead of writing an empty snapshot to avoid
 * leaving a stale `{isEditMode:false, …empty}` blob in sessionStorage after a
 * Discard / Save.
 */
export const getManageContactsEditPersistenceListener: GetAppListener = (addAppListener) =>
  addAppListener({
    matcher: isAnyOf(
      enterEditMode,
      changeDraftField,
      undoRowEdit,
      exitEditMode,
      markRowForRemoval,
      unmarkRowForRemoval
    ),
    effect: (action, { getState }) => {
      if (exitEditMode.match(action)) {
        clearEditStateFromStorage();

        return;
      }
      saveEditStateToStorage(editSelector(getState()));
    },
  });

/**
 * Clears the persisted Manage Contacts edit snapshot when an impersonation session
 * starts or stops. The root reducer already wipes the in-memory slice for those
 * actions; we must also drop the sessionStorage mirror so a session switch never
 * resurrects the previous user's pending edits.
 */
export const getManageContactsImpersonationListener: GetAppListener = (addAppListener) =>
  addAppListener({
    matcher: isAnyOf(startImpersonationSession, stopImpersonationSession),
    effect: () => {
      clearEditStateFromStorage();
    },
  });
